import { Controller } from "stimulus"

// Keeps the locked chat layout pinned to the *visual* viewport instead of
// `100dvh`. On mobile, opening the keyboard slides the visual viewport up while
// the layout viewport (and `dvh`) either lags or stays put, so the browser
// scroll-into-views the focused textarea against our `overflow: hidden` body —
// that fight is the flicker/upward-shift the user sees before it settles.
//
// By writing `visualViewport.height` into a CSS variable the body height reads,
// the layout shrinks in step with the keyboard, the input naturally rests just
// above it, and the browser has nothing left to scroll.
export default class extends Controller {
  connect() {
    this.viewport = window.visualViewport
    // No visualViewport (older/desktop browsers): leave the `100dvh` fallback in
    // place — the keyboard flicker this fixes only happens where the API exists.
    if (!this.viewport) return

    this.rafId = null
    this._update = this.scheduleUpdate.bind(this)

    this.viewport.addEventListener("resize", this._update)
    this.viewport.addEventListener("scroll", this._update)
    this.applyHeight()
  }

  disconnect() {
    if (!this.viewport) return

    this.viewport.removeEventListener("resize", this._update)
    this.viewport.removeEventListener("scroll", this._update)
    if (this.rafId) cancelAnimationFrame(this.rafId)
    document.documentElement.style.removeProperty("--chat-viewport-height")
  }

  // The keyboard animation fires resize/scroll rapidly; coalesce to one write
  // per frame so we never lay out mid-frame (which would reintroduce flicker).
  scheduleUpdate() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null
      this.applyHeight()
    })
  }

  applyHeight() {
    document.documentElement.style.setProperty(
      "--chat-viewport-height",
      `${this.viewport.height}px`
    )
  }
}
